import * as types from '@/store/mutation-types';
import _ from 'lodash';

const state = {
  account: {},
  priceSource: false,
  websites: [],
  fetchError: false,
  online: true,
  offlineWarnings: [],
  userPool: null,
  identityId: null,
  route: 'admin-dashboard',
  firstLogin: false,
  reLoginRoute: {},
  publishingBoilerplate: false,
  useStaging: false,
  browser: {},
  userSession: {},
  cache: {},
  globalLoading: {
    userData: false,
    websites: false,
    pages: false,
    dynamic: false,
    drafts: false,
    recycled: false,
    templates: false,
  },
};

const getters = {
  account(state) {
    return state.account;
  },
  priceSource(state) {
    return state.priceSource;
  },
  websites(state) {
    return state.websites;
  },
  online(state) {
    return state.online;
  },
  route(state) {
    return state.route;
  },
  userPool(state) {
    return state.userPool;
  },
  identityId(state) {
    return state.identityId;
  },
  getReLoginRoute(state) {
    return state.reLoginRoute;
  },
  fetchError(state) {
    return state.fetchError;
  },
  useStaging(state) {
    return state.useStaging;
  },
  firstLogin(state) {
    return state.firstLogin;
  },
  publishingBoilerplate(state) {
    return state.publishingBoilerplate;
  },
  browser(state) {
    return state.browser;
  },
  userSession(state) {
    return state.userSession;
  },
  cache(state) {
    return state.cache;
  },
  globalLoading(state) {
    return state.globalLoading;
  },
};

const mutations = {
  [types.SET_ACCOUNT](state, p) {
    this._vm.$log.debug('SET_ACCOUNT: ', p);
    state.account = p;
  },
  [types.UPDATE_ACCOUNT](state, p) {
    this._vm.$log.debug('UPDATE_ACCOUNT: ', p);
    _.extend(state.account, p);
  },
  [types.SET_PRICE_SOURCE](state, p) {
    this._vm.$log.debug('SET_PRICE_SOURCE: ', p);
    state.priceSource = p.data;
  },
  [types.SET_WEBSITES](state, p) {
    this._vm.$log.debug('SET_WEBSITES', p);
    state.websites = p.websites;
  },
  [types.UPDATE_ONLINE_STATUS](state, p) {
    state.online = p.online;
  },
  [types.SET_PUBLISH_BOILERPLATE](state, p) {
    state.publishingBoilerplate = p;
  },
  [types.EMPTY_OFFLINE_WARNINGS](state) {
    state.offlineWarnings = [];
  },
  [types.ADD_OFFLINE_WARNING](state, p) {
    state.offlineWarnings.push(p.id);
  },
  [types.UPDATE_ROUTE](state, p) {
    state.route = p;
  },
  [types.UPDATE_RELOGIN](state, p) {
    state.reLoginRoute = p;
  },
  [types.SET_FETCH_ERROR](state, p) {
    state.fetchError = p;
  },
  [types.SET_FIRST_LOGIN](state, p) {
    state.firstLogin = p;
  },
  [types.SET_USER_POOL](state, p) {
    this._vm.$log.debug('SET_USER_POOL', p);
    state.userPool = p;
  },
  [types.SET_USER_IDENTITY_ID](state, p) {
    this._vm.$log.debug('SET_USER_IDENTITY_ID', p);
    state.identityId = p;
  },
  [types.SET_ALERT_STATUS](state, p) {
    p.item.status = p.status;
  },
  [types.SET_DATA_ORIGIN](state, p) {
    state.useStaging = p;
  },
  [types.REMOVE_ALERT](state, p) {
    state.account.alerts.splice(p.i, 1);
  },
  [types.ADD_ALERT](state, p) {
    state.account.alerts.splice(0, 0, p);
  },
  [types.SET_GLOBAL_NEW_VALUES](state, p) {
    _.forEach(p.values, (item) => {
      p.vm.$set(p.item, item.key, item.value);
    });
  },
  [types.SET_GLOBAL_VALUE](state, p) {
    if (typeof p.item[p.key] === 'undefined') {
      let val = p.value;
      if (p.array) {
        val = [p.value];
      }
      this._vm.$set(p.item, p.key, val);
    } else if (p.array) {
      p.item[p.key].push(p.value);
    } else {
      p.item[p.key] = p.value;
    }
  },
  [types.READD_GLOBAL_ARRAY](state, p) {
    const index = p.list.findIndex(
      item => (_.isObject(item) && item[p.key] === p.value) || item === p.value,
    );
    if (index > -1) {
      p.list.splice(index, 1, p.payload);
    } else {
      p.list.push(p.payload);
    }
  },
  [types.RUN_STATE_FN](state, p) {
    p.fn();
  },
  [types.SET_BROWSER](state, p) {
    _.forOwn(p, (value, key) => {
      this._vm.$set(state.browser, key, value);
    });
  },
  [types.SET_USER_SESSION](state, p) {
    state.userSession = p.session;
    this._vm.$log.debug('SET_USER_SESSION', p.session);
  },
  [types.SET_GLOBAL_LOADER](state, { type, value }) {
    state.globalLoading[type] = value;
  },
};

const actions = {
  setPriceSource({ commit }, payload) {
    commit(types.SET_PRICE_SOURCE, payload);
  },
  async setWebsites({ commit }, payload) {
    commit(types.SET_WEBSITES, payload);
  },
  setFetchError: ({ commit }, p) => {
    commit(types.SET_FETCH_ERROR, p);
  },
  setFirstLogin: ({ commit }, p) => {
    commit(types.SET_FIRST_LOGIN, p);
  },
  setDataOrigin: ({ commit }, p) => {
    commit(types.SET_DATA_ORIGIN, p);
  },
  setPublishBoilerplate: ({ commit }, p) => {
    commit(types.SET_PUBLISH_BOILERPLATE, p);
  },
  updateOnlineStatus({ commit, state, dispatch, rootState }, payload) {
    commit(types.UPDATE_ONLINE_STATUS, payload);
    const w = state.offlineWarnings;
    if (payload.online) {
      payload.type = 'success';
      // check for lingering offline warnings
      if (w.length > 0) {
        _.forEach(w, (n) => {
          const i = _.findIndex(rootState.Notifications.notifications, o => o.id === n);
          // if a warning id is being shown then remove it
          if (i > -1) {
            commit(types.REMOVE_NOTIFICATION, i);
          }
        });
        // clear all empty warning checks
        commit(types.EMPTY_OFFLINE_WARNINGS);
      }
      dispatch('addNotification', payload);
    } else if (!payload.online && w.length < 1) {
      commit(types.ADD_OFFLINE_WARNING, {
        id: payload.id,
      });
      payload.type = 'warning';
      dispatch('addNotification', payload);
    }
  },
  updateRoute({ commit }, payload) {
    commit(types.UPDATE_ROUTE, payload);
  },
  setAccount({ commit }, payload) {
    commit(types.SET_ACCOUNT, payload);
  },
  setUserSession({ commit }, payload) {
    commit(types.SET_USER_SESSION, payload);
  },
  updateAccount({ commit }, payload) {
    commit(types.UPDATE_ACCOUNT, payload);
  },
  updateReLogin({ commit }, payload) {
    commit(types.UPDATE_RELOGIN, payload);
  },
  setUserPool({ commit }, payload) {
    commit(types.SET_USER_POOL, payload);
  },
  setUserIdentitiyId({ commit }, payload) {
    commit(types.SET_USER_IDENTITY_ID, payload);
  },
  setAlertStatus({ commit }, payload) {
    commit(types.SET_ALERT_STATUS, payload);
  },
  removeAlert({ commit }, payload) {
    commit(types.REMOVE_ALERT, payload);
  },
  addAlert({ commit }, payload) {
    commit(types.ADD_ALERT, payload);
  },
  setGlobalNewValues({ commit }, payload) {
    commit(types.SET_GLOBAL_NEW_VALUES, payload);
  },
  setGlobalValue({ commit }, payload) {
    commit(types.SET_GLOBAL_VALUE, payload);
  },
  readdGlobalArray({ commit }, payload) {
    commit(types.READD_GLOBAL_ARRAY, payload);
  },
  runStateFn({ commit }, payload) {
    commit(types.RUN_STATE_FN, payload);
  },
  setBrowser({ commit }, payload) {
    commit(types.SET_BROWSER, payload);
  },
  setGlobalLoader({ commit }, payload) {
    commit(types.SET_GLOBAL_LOADER, payload);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};



// WEBPACK FOOTER //
// ./src/global-view/vuex.js